import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Package, Plus, LogOut, MessageSquare, FileText, Menu,
  MapPin, Calendar, CheckCircle2, Clock, Truck, AlertCircle
} from 'lucide-react';
import api, { jobsAPI } from '../../utils/api';
import LanguageSelector from '../../components/LanguageSelector';

const CustomerDashboard = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    // Check if customer needs to complete setup
    if (!user?.profile_completed) {
      navigate('/mobile/setup');
      return;
    }
    fetchJobs();
  }, [user, navigate]);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.list();
      const customerJobs = response.data.filter(j => j.customer_id === user?.id);
      setJobs(customerJobs);
      
      // Find active job (not completed or cancelled)
      const active = customerJobs.find(j => !['completed', 'cancelled'].includes(j.status));
      setActiveJob(active);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/mobile-login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getJobProgress = (status) => {
    switch (status) {
      case 'pending': return 20;
      case 'confirmed': return 40;
      case 'scheduled': return 60;
      case 'in_progress': return 80;
      case 'completed': return 100;
      default: return 0;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="customer-dashboard">
      {/* Mobile Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-8" />
              <div>
                <h1 className="text-lg font-bold gradient-text">MoveMate</h1>
                <p className="text-xs text-gray-500">{t('mobile.customer_portal')}</p>
              </div>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2">
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="border-t bg-white">
            <div className="p-4 space-y-2">
              <div className="mb-4">
                <p className="text-sm font-semibold">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500">{user?.phone || user?.email}</p>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">{t('common.language')}</span>
                <LanguageSelector variant="compact" />
              </div>
              <Button variant="outline" className="w-full" onClick={() => navigate('/mobile/setup')}>
                <FileText size={16} className="mr-2" />
                {t('staff.edit_details')}
              </Button>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-6 pb-20">
        {/* Welcome Section */}
        <div>
          <h2 className="text-2xl font-bold mb-1">{t('common.hello')}, {user?.first_name}!</h2>
          <p className="text-gray-600">{t('mobile.track_your_move')}</p>
        </div>

        {/* Active Job Card */}
        {activeJob ? (
          <Card className="border-2 border-primary">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{t('jobs.your_move')}</CardTitle>
                  <CardDescription>{activeJob.job_number}</CardDescription>
                </div>
                <Badge className={getStatusColor(activeJob.status)}>
                  {t(`jobs.status.${activeJob.status}`) || activeJob.status.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('jobs.progress')}</span>
                  <span>{getJobProgress(activeJob.status)}%</span>
                </div>
                <Progress value={getJobProgress(activeJob.status)} className="h-2" />
              </div>

              {/* Route */}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin size={14} />
                    <span>{t('jobs.from_address').split(' ')[0]}</span>
                  </div>
                  <p className="font-medium">{activeJob.from_address?.city}</p>
                </div>
                <Truck className="text-primary" size={24} />
                <div className="flex-1 text-right">
                  <div className="flex items-center gap-2 text-gray-500 justify-end">
                    <MapPin size={14} />
                    <span>{t('jobs.to_address').split(' ')[0]}</span>
                  </div>
                  <p className="font-medium">{activeJob.to_address?.city}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Calendar size={12} />
                    <span>{t('jobs.collection')}</span>
                  </div>
                  <p className="font-semibold text-sm">
                    {activeJob.move_out_date 
                      ? new Date(activeJob.move_out_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short' 
                        })
                      : 'TBD'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Calendar size={12} />
                    <span>{t('jobs.delivery')}</span>
                  </div>
                  <p className="font-semibold text-sm">
                    {activeJob.move_in_date 
                      ? new Date(activeJob.move_in_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short' 
                        })
                      : 'Same day'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="h-12"
                  onClick={() => navigate(`/mobile/inventory?jobId=${activeJob.id}`)}
                >
                  <Package size={18} className="mr-2" />
                  {t('nav.inventory')}
                </Button>
                <Button 
                  className="h-12"
                  onClick={() => navigate(`/mobile/chat?jobId=${activeJob.id}`)}
                >
                  <MessageSquare size={18} className="mr-2" />
                  {t('nav.chat')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="font-semibold text-lg mb-2">{t('mobile.no_active_jobs')}</h3>
              <p className="text-gray-500 text-sm mb-4">
                {t('errors.something_went_wrong')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Card 
            className="card-hover cursor-pointer" 
            onClick={() => activeJob && navigate(`/mobile/inventory?jobId=${activeJob.id}`)}
          >
            <CardContent className="pt-6 text-center">
              <Package className="mx-auto text-primary mb-2" size={32} />
              <p className="font-semibold text-sm">{t('nav.inventory')}</p>
              {activeJob && (
                <p className="text-xs text-gray-500">{t('common.view')}/{t('common.add')}</p>
              )}
            </CardContent>
          </Card>

          <Card 
            className="card-hover cursor-pointer"
            onClick={() => activeJob && navigate(`/mobile/chat?jobId=${activeJob.id}`)}
          >
            <CardContent className="pt-6 text-center">
              <MessageSquare className="mx-auto text-primary mb-2" size={32} />
              <p className="font-semibold text-sm">{t('nav.chat')}</p>
              <p className="text-xs text-gray-500">{t('chat.chat_with_company')}</p>
            </CardContent>
          </Card>

          <Card 
            className="card-hover cursor-pointer"
            onClick={() => activeJob && navigate(`/mobile/estimate?jobId=${activeJob.id}`)}
          >
            <CardContent className="pt-6 text-center">
              <FileText className="mx-auto text-primary mb-2" size={32} />
              <p className="font-semibold text-sm">{t('nav.estimates')}</p>
              <p className="text-xs text-gray-500">{t('mobile.view_estimate')}</p>
            </CardContent>
          </Card>

          <Card 
            className="card-hover cursor-pointer"
            onClick={() => navigate('/mobile/setup')}
          >
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="mx-auto text-primary mb-2" size={32} />
              <p className="font-semibold text-sm">{t('settings.edit_profile')}</p>
              <p className="text-xs text-gray-500">{t('common.update')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Job History */}
        {jobs.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {jobs.filter(j => j.id !== activeJob?.id).map((job) => (
                  <div
                    key={job.id}
                    className="p-3 border rounded-lg flex justify-between items-center"
                    onClick={() => navigate(`/mobile/job/${job.id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{job.job_number}</p>
                      <p className="text-xs text-gray-500">
                        {job.from_address?.city} → {job.to_address?.city}
                      </p>
                    </div>
                    <Badge className={getStatusColor(job.status)} variant="outline">
                      {job.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CustomerDashboard;
